/**
 * TablesService
 *
 * This service handles all custom table-related operations in the application.
 * It provides methods for managing custom table templates and their instances.
 * The service supports CRUD operations for both table templates and table instances,
 * with proper tenant isolation and authentication.
 *
 * @class
 */

import HttpService from './HttpService';
import type { ResponseType } from 'axios';
import {
  pathCustomTables,
  pathCustomTablesHolding,
  pathCustomTableId,
  pathCustomTableRows,
  pathCustomTableColumnsRows,
  pathCustomTablesHoldingId,
  pathCustomTablesHoldingRows,
  pathAddTableToMatter,
  pathAvailableTablesForMatter,
  pathDeleteCustomTable,
  pathDeleteCustomTableHolding,
} from '@frontend/__settings__/septSoftwarePaths';
import { AddTableToMatterRequest, AddTableToMatterResponse } from '@frontend/Models/Table/CustomTableRequestResponseModels';
import {
  GetCustomTablesResponse,
  GetCustomTableHoldingResponse,
  GetCustomTablesHoldingResponse,
  CreateCustomTableResponse,
  CreateCustomTableHoldingResponse,
  UpdateCustomTableResponse,
  UpdateCustomTableHoldingResponse,
  CustomTable,
} from '@frontend/Models/Table/CustomTableModels';

const http = new HttpService();
const excelConfig = { responseType: 'blob' as ResponseType };

class TablesService {
  /**
   * Creates a new custom table template
   *
   * @param {Object} tableData - The table template data
   * @param {string} tableData.table_name - Name of the table
   * @param {Array} tableData.column_value - Column definitions
   * @param {Array} tableData.rows_value - Row definitions
   * @returns {Promise<CreateCustomTableResponse>} Created table template data
   * @throws {Error} If the API request fails
   */
  static async createCustomTable(tableData: any): Promise<CreateCustomTableResponse> {
    return await http.post<CreateCustomTableResponse>(pathCustomTables, tableData);
  }

  /**
   * Retrieves all custom table templates for the current tenant
   *
   * @returns {Promise<GetCustomTablesResponse>} Array of table templates
   * @throws {Error} If the API request fails
   */
  static async getCustomTables(): Promise<GetCustomTablesResponse> {
    return await http.get<GetCustomTablesResponse>(pathCustomTables);
  }

  /**
   * Retrieves a specific custom table template by ID
   *
   * @param {number} tableId - ID of the table template
   * @returns {Promise<CustomTable>} Table template data
   * @throws {Error} If the API request fails
   */
  static async getCustomTableById(tableId: number): Promise<CustomTable> {
      return await http.get<CustomTable>(pathCustomTableId(tableId));
  }

  /**
   * Deletes a custom table template.
   * If cascade is true, associated holdings are also removed.
   */
  static async deleteCustomTable(tableId: number, cascade = false): Promise<{ message?: string; holding_count?: number }> {
    const url = `${pathDeleteCustomTable(tableId)}${cascade ? '?cascade=true' : ''}`;
    return http.delete<{ message?: string; holding_count?: number }>(url);
  }

  /**
   * Creates a new custom table instance
   *
   * @param {Object} holdingData - The table instance data
   * @param {number} holdingData.ct_id - Template ID
   * @param {number} holdingData.matterid - Matter ID
   * @param {string} holdingData.table_name - Name of the table
   * @param {Array} holdingData.column_value - Column definitions
   * @param {Array} holdingData.rows_value - Row definitions
   * @param {Object} holdingData.value - Table values
   * @returns {Promise<CreateCustomTableHoldingResponse>} Created table instance data
   * @throws {Error} If the API request fails
   */
  static async createCustomTableHolding(holdingData: any): Promise<CreateCustomTableHoldingResponse> {
    return await http.post<CreateCustomTableHoldingResponse>(pathCustomTablesHolding, holdingData);
  }

  /**
   * Retrieves all custom table instances for the current tenant
   *
   * @returns {Promise<GetCustomTablesHoldingResponse>} Array of table instances
   * @throws {Error} If the API request fails
   */
  static async getCustomTablesHolding(): Promise<GetCustomTablesHoldingResponse> {
    return await http.get<GetCustomTablesHoldingResponse>(pathCustomTablesHolding);
  }

  /**
   * Retrieves a specific custom table instance by ID
   *
   * @param {number} holdingId - ID of the table instance
   * @returns {Promise<GetCustomTableHoldingResponse>} Table instance data
   * @throws {Error} If the API request fails
   */
  static async getCustomTableHoldingById(holdingId: number): Promise<GetCustomTableHoldingResponse> {
    return await http.get<GetCustomTableHoldingResponse>(pathCustomTablesHoldingId(holdingId));
  }

  /**
   * Replaces all rows in a custom table
   * @param {number} tableId - ID of the table
   * @param {Array} newRowsValue - The new array of rows
   * @returns {Promise<UpdateCustomTableResponse>} Update confirmation
   * @throws {Error} If the API request fails
   */
  static async replaceAllRowsInCustomTable(tableId: number, newRowsValue: any): Promise<UpdateCustomTableResponse> {
    return await http.patch<UpdateCustomTableResponse>(pathCustomTableRows(tableId), { rows_value: newRowsValue });
  }

  /**
   * Updates the columns and/or rows of a custom table
   * @param {number} tableId - ID of the table
   * @param {Object} data - Object with optional column_value and/or rows_value
   * @returns {Promise<UpdateCustomTableResponse>} Updated table data
   * @throws {Error} If the API request fails
   */
  static async updateCustomTableColumnsRows(tableId: number, data: any): Promise<UpdateCustomTableResponse> {
    return await http.put<UpdateCustomTableResponse>(pathCustomTableColumnsRows(tableId), data);
  }

  /**
   * Retrieves all custom table instances for a specific matter
   * @param {string|number} matterid - The matter/project ID
   * @returns {Promise<GetCustomTablesHoldingResponse>} Array of custom table holdings for the matter
   * @throws {Error} If the API request fails
   */
  static async getCustomTablesHoldingByMatterId(matterid: string | number): Promise<GetCustomTablesHoldingResponse> {
      return await http.get<GetCustomTablesHoldingResponse>(`${pathCustomTablesHolding}?matterid=${matterid}`);
  }

  /**
   * Replaces all rows in a custom table holding (instance)
   * @param {number} holdingId - ID of the table holding (instance)
   * @param {Array|Object} newRowsValue - The new rows value to set
   * @returns {Promise<UpdateCustomTableHoldingResponse>} Update confirmation
   * @throws {Error} If the API request fails
   */
  static async replaceAllRowsInCustomTableHolding(holdingId: number, newRowsValue: any): Promise<UpdateCustomTableHoldingResponse> {
      return await http.patch<UpdateCustomTableHoldingResponse>(pathCustomTablesHoldingRows(holdingId), { rows_value: newRowsValue });
  }

  /**
   * Adds a custom table to a matter
   * @param {Object} data - The data for adding the table
   * @param {number} data.ct_id - Template ID
   * @param {string|number} data.matterid - Matter ID
   * @param {string} data.table_name - Name of the table
   * @returns {Promise<AddTableToMatterResponse>} Response with holding_id and other details
   * @throws {Error} If the API request fails
   */
  static async addTableToMatter(data: AddTableToMatterRequest): Promise<AddTableToMatterResponse> {
    return http.post<AddTableToMatterResponse>(pathAddTableToMatter, data);
  }

  /**
   * Get available custom table templates that can be added to a matter
   * @param matterId - Matter/project ID
   * @returns Response with available_tables array or array of CustomTable
   */
  static async getAvailableTablesForMatter(matterId: string | number): Promise<{ available_tables?: CustomTable[] } | CustomTable[]> {
    return http.get<{ available_tables?: CustomTable[] } | CustomTable[]>(pathAvailableTablesForMatter(String(matterId)));
  }

  /**
   * Delete a custom table holding (instance) from a matter
   * @param holdingId - ID of the table holding
   */
  static async deleteCustomTableHolding(holdingId: number): Promise<{ message?: string }> {
    return http.delete<{ message?: string }>(pathDeleteCustomTableHolding(holdingId));
  }

  /**
   * Exports a custom table to Excel format
   * @param {number} holdingId - ID of the table holding (instance)
   * @param {string[]} columnIds - Optional array of column IDs to export. If not provided, all columns are exported
   * @returns {Promise<Blob>} Excel file as a Blob
   * @throws {Error} If the API request fails
   */
  static async exportCustomTableToExcel(holdingId: number, columnIds?: string[]): Promise<Blob> {
    const payload = columnIds ? { columnIds } : {};
    return http.post<Blob>(`customTables/excel/${holdingId}`, payload, excelConfig);
  }
}

export default TablesService;

